// The Record's header actions and the right column's Mortgagee, Files and Portal log cards (spec
// C7.4, Record board; the Notes card is in notes.tsx), and the step list the Quick view shows. Open in
// AMS360, the files and the Portal log are real; the actions whose feature is not built yet are
// disabled with the reason (shell/placeholders.ts).
import { useState } from 'react';
import type { AddedFile, Api, FetchView, LogEntry, RenewalDetail, StepsView } from '../auth/api';
import { Tip } from '../shell/Tip';
import { managesDepartment, type Role } from '../roles';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { PlaceholderButton } from '../shell/Placeholder';
import { Toast } from '../shell/Toast';
import { usePopover } from '../shell/use-popover';
import { Viewer, type ViewerFile } from '../shell/Viewer';
import {
  BankIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  DotsIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  SendIcon,
  SwapIcon,
  UserCheckIcon,
  TrashIcon,
  UploadIcon,
} from '../shell/icons';
import { docLabel, decStamp, splitDocs, stampTime } from './fetch-lines';
import { FileUploadDialog } from './FileUploadDialog';
import {
  alreadyThereText,
  canAddFiles,
  cannotUndoText,
  deletedText,
  deleteLine,
  deleteTitle,
  fileTypeLabel,
  restoredText,
  uploadedFileText,
} from './files-lines';
import { LastTermFold } from './LastTermFold';
import { stageOf } from './renewal-lines';
import { bankHidden, nothingToMark, shownSteps, stepTitle } from './step-lines';
import { branchMenu } from './branch-lines';

/** What the "..." menu asks the Steps card for (S16, S17). */
export type StepsMenuAsk = 'close' | 'previously' | 'check' | 'non_renewal' | 'inactive' | 'back';

/** Open in AMS360 (Customer, Dec page), Reassign, Hand to AE and the "..." menu (spec C7.4). */
export function RecordActions({
  r,
  role,
  meId,
  steps = null,
  onReassign,
  onHand,
  onSteps,
  noReassign = false,
  onTakeOver = null,
}: {
  r: RenewalDetail;
  role: Role;
  /** The signed-in person: a Renewal VA acts only on the renewals they handle. */
  meId: string;
  /** The steps as the Steps card shows them: who works them, and what is left to mark. */
  steps?: StepsView | null;
  onReassign: () => void;
  /** Hand to AE (spec C6): opens the dialog. */
  onHand: () => void;
  /**
   * "..." menu: Mark steps done previously or Close renewal (S16), and the branches (S17), opened
   * by the Steps card.
   */
  onSteps?: (what: StepsMenuAsk) => void;
  /** Leaves Reassign out: the handoff waits on this AE, who answers it first (spec C7.4). */
  noReassign?: boolean;
  /** Take over (the AE, spec C6, decision 1): a confirm; null where they may not. */
  onTakeOver?: (() => void) | null;
}) {
  const { open: amsOpen, setOpen: setAmsOpen, ref: amsRef } = usePopover();
  const { open: moreOpen, setOpen: setMoreOpen, ref: moreRef } = usePopover();
  const manager = managesDepartment(role);
  const closed = stageOf(r) === 'closed';
  // A Renewal VA works the renewals they handle; one they sent to an AE who accepted it is read
  // only for them until it comes back (spec C6).
  const works = (role === 'Renewal VA' && r.handledBy?.id === meId) || manager;
  // Mark steps done previously and Close renewal: whoever works the record, which includes the
  // Account Executive who handles it (the server's canWork).
  const working = !closed && !!onSteps && (steps ? steps.canWork : works);
  const toMark = !!steps && !nothingToMark(steps);
  const onHandoff =
    r.handoff?.state === 'accepted' &&
    r.handoff.to.id === meId &&
    r.handoff.kind !== 'reassign_to_ae';
  const branch = branchMenu(r, steps?.steps.done.state === 'done');
  const stepsItem = (what: StepsMenuAsk, label: string, off: boolean) => (
    <button
      type="button"
      className="mi"
      role="menuitem"
      aria-haspopup={what === 'check' ? undefined : 'dialog'}
      disabled={off}
      onClick={() => {
        setMoreOpen(false);
        onSteps?.(what);
      }}
    >
      {label}
    </button>
  );
  // Reassign: the VA their own, the AE to themselves, the Manager and Admin anything.
  const reassigns = (works || role === 'Account Executive') && !noReassign;
  return (
    <div className="row g8 wrap">
      <div ref={amsRef} className="rel">
        <button
          type="button"
          className="btn"
          aria-haspopup="menu"
          aria-expanded={amsOpen}
          onClick={() => setAmsOpen(!amsOpen)}
        >
          <ExternalLinkIcon className="ico s14" />
          Open in AMS360
          <ChevronDownIcon className="ico s12 muted" />
        </button>
        {amsOpen && (
          <>
            <button
              type="button"
              className="pop-scrim"
              aria-label="Close menu"
              onClick={() => setAmsOpen(false)}
            />
            <div className="menu rmenu" role="menu" aria-label="Open in AMS360">
              <a
                className="mi"
                role="menuitem"
                href={r.ams.customer}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAmsOpen(false)}
              >
                Customer
              </a>
              <a
                className="mi"
                role="menuitem"
                href={r.ams.decPage}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAmsOpen(false)}
              >
                Dec page
              </a>
            </div>
          </>
        )}
      </div>
      {reassigns && !closed && (
        <button type="button" className="btn" aria-haspopup="dialog" onClick={onReassign}>
          <SwapIcon className="ico s14" />
          Reassign
        </button>
      )}
      {/* Take over: the AE, next to Reassign where the VA has Hand to AE (the Record board). */}
      {onTakeOver && (
        <button type="button" className="btn" onClick={onTakeOver}>
          <UserCheckIcon className="ico s14" />
          Take over
        </button>
      )}
      {/* Hand to AE: the VA their own, the Manager and the Admin; not while a handoff is in play. */}
      {works && !closed && !r.handoff && (
        <button type="button" className="btn" aria-haspopup="dialog" onClick={onHand}>
          <SendIcon className="ico s14" />
          Hand to AE
        </button>
      )}
      <div ref={moreRef} className="rel">
        <button
          type="button"
          className="btn btn-icon"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(!moreOpen)}
        >
          <DotsIcon className="ico s14" />
        </button>
        {moreOpen && (
          <>
            <button
              type="button"
              className="pop-scrim"
              aria-label="Close menu"
              onClick={() => setMoreOpen(false)}
            />
            <div className="menu rmenu" role="menu" aria-label="More actions">
              {/* The branches (spec C8, S17): switch to one, or Back to renewal steps. */}
              {works && !closed && !r.branch && (
                <>
                  {branch.enter ? (
                    <>
                      {stepsItem('non_renewal', 'Non-renewal', !steps)}
                      {stepsItem('inactive', 'Policy inactive', !steps)}
                    </>
                  ) : (
                    <>
                      <Tip text={branch.why ?? ''} focusable>
                        {stepsItem('non_renewal', 'Non-renewal', true)}
                      </Tip>
                      <Tip text={branch.why ?? ''} focusable>
                        {stepsItem('inactive', 'Policy inactive', true)}
                      </Tip>
                    </>
                  )}
                </>
              )}
              {works && !closed && r.branch && (
                <>
                  {branch.back ? (
                    stepsItem('back', 'Back to renewal steps', !steps)
                  ) : (
                    <Tip text={branch.why ?? ''} focusable>
                      {stepsItem('back', 'Back to renewal steps', true)}
                    </Tip>
                  )}
                </>
              )}
              {working && !r.branch && (
                <>
                  {toMark || !steps ? (
                    stepsItem('previously', 'Mark steps done previously', !steps)
                  ) : (
                    <Tip text="Steps 1 to 5 are done or waiting on the insured." focusable>
                      {stepsItem('previously', 'Mark steps done previously', true)}
                    </Tip>
                  )}
                  {/* The AE on an accepted handoff closes through Finish handoff instead; an AE
                      who holds it by a reassign keeps Close renewal (spec C6, S15 batch 6c). */}
                  {!onHandoff && stepsItem('close', 'Close renewal', !steps)}
                  {/* Renew manually instead was chosen on step 2 (S16 Batch 3): the way back. */}
                  {steps?.download?.manual &&
                    steps.steps.ams360.state !== 'done' &&
                    stepsItem('check', 'Back to Check AMS360', false)}
                </>
              )}
              {((works && !closed) || working) && <div className="vm-div" />}
              {manager ? (
                <PlaceholderButton id="record.delete" className="mi danger" block>
                  <TrashIcon />
                  Delete record
                </PlaceholderButton>
              ) : (
                <PlaceholderButton id="record.requestDelete" className="mi danger" block>
                  <TrashIcon />
                  Request delete
                </PlaceholderButton>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="kv">
      <dt>{label}</dt>
      <dd>
        <span>{value}</span>
        <button
          type="button"
          className="ibtn"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(
              () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              },
              () => undefined,
            );
          }}
        >
          {copied ? <span className="cpd">Copied</span> : <CopyIcon className="ico s14" />}
        </button>
      </dd>
    </div>
  );
}

/**
 * Mortgagee: the bank on the renewal dec page, with copy buttons (Record board). AMS360's Additional
 * Interests are not in the sync yet, so this is what the dec page says, and the card says so. Not
 * shown when there is no mortgagee.
 */
export function MortgageeCard({ steps }: { steps: StepsView | null }) {
  const list = steps?.bank.mortgagees ?? [];
  const empty = !steps
    ? 'Shows here once the dec page is read.'
    : steps.bank.unread
      ? 'The dec page could not be read. Check the PDF for a mortgagee.'
      : !steps.decPage.attached || steps.decPage.reading !== 'done'
        ? 'Shows here once the dec page is read.'
        : null;
  // No mortgagee: the card is not shown at all (Yehuda, Oct 29), like Proof to bank. That is a
  // read dec page without one, or the VA's "Not needed" on a closed renewal.
  const noMortgagee = !!steps && !empty && list.length === 0;
  if (noMortgagee || (steps?.steps.bank.state === 'done' && bankHidden(steps))) return null;
  return (
    <section className="card" aria-labelledby="mort-title">
      <div className="card-h">
        <h2 id="mort-title" className="card-t">
          <BankIcon className="ico s14" />
          Mortgagee
        </h2>
        {!empty && <span className="t11 faint">from the dec page</span>}
      </div>
      {empty ? (
        <div className="emp">
          <BankIcon className="ico s14" />
          <span>{empty}</span>
        </div>
      ) : (
        list.map((m, i) => (
          <dl key={i} className="m0">
            <CopyValue label="Bank" value={m.name} />
            {m.loanNumber && <CopyValue label="Loan number" value={m.loanNumber} />}
            {m.clause && <CopyValue label="Clause" value={m.clause} />}
          </dl>
        ))
      )}
    </section>
  );
}

type FilesToast = { id: number; text: string; undo: (() => Promise<void>) | null };

/**
 * Files: every file on the renewal with View and Download, Upload PDF for any PDF, and Delete on the
 * files added here (Record board, S14). The dec page the steps use is replaced in step 1.
 */
export function FilesCard({
  api,
  r,
  fetch,
  onChanged,
}: {
  api: Api;
  r: RenewalDetail;
  fetch: FetchView | null;
  /** A file was added, deleted or brought back: the card and the Portal log are read again. */
  onChanged: () => void;
}) {
  const [file, setFile] = useState<ViewerFile | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<AddedFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<FilesToast | null>(null);
  const docs = fetch?.documents ?? [];
  const added = fetch?.files ?? [];
  const works = canAddFiles(fetch);
  const carrier = fetch?.carrier?.name ?? r.carrier;
  const subline = [r.insured, carrier ?? 'Carrier not in AMS360', r.line, r.policyNumber].join(
    ' | ',
  );
  const show = (text: string, undo: FilesToast['undo'] = null) =>
    setToast((last) => ({ id: (last?.id ?? 0) + 1, text, undo }));
  // This term's files on top; last term's dec page in a closed list below (Yehuda, Oct 28).
  const files = splitDocs(docs);
  const view = (id: string, name: string) =>
    setFile({
      title: name,
      subline,
      src: api.documentUrl(id),
      downloadHref: api.documentUrl(id, true),
      kind: 'pdf',
    });
  const actions = (id: string, name: string) => (
    <>
      <button
        type="button"
        className="btn btn-sm"
        aria-haspopup="dialog"
        onClick={() => view(id, name)}
      >
        View
      </button>
      <a className="btn btn-sm" href={api.documentUrl(id, true)}>
        <DownloadIcon className="ico s12" />
        Download
      </a>
    </>
  );
  const fileRow = (d: (typeof docs)[number]) => (
    <div key={d.id} className="frow2">
      <FileIcon className="ico s14" />
      <div className="col g2 grow" style={{ minWidth: 0 }}>
        <span className="t13 ink ell">{d.fileName}</span>
        <span className="t11 muted">
          {docLabel(d)}, {decStamp(d, carrier)}
        </span>
      </div>
      {actions(d.id, d.fileName)}
    </div>
  );
  const addedRow = (f: AddedFile) => (
    <div key={f.id} className="frow2">
      <FileIcon className="ico s14" />
      <div className="col g2 grow" style={{ minWidth: 0 }}>
        <span className="t13 ink ell">{f.fileName}</span>
        <span className="t11 muted">
          {fileTypeLabel(f.type)}, uploaded by {f.by ?? 'someone'}, {stampTime(f.createdAt)}
        </span>
      </div>
      {actions(f.id, f.fileName)}
      {works && (
        <button
          type="button"
          className="btn btn-sm btn-del"
          aria-haspopup="dialog"
          aria-label={`Delete ${f.fileName}`}
          onClick={() => setDeleting(f)}
        >
          <TrashIcon className="ico s12" />
          Delete
        </button>
      )}
    </div>
  );

  async function remove(f: AddedFile) {
    setBusy(true);
    const res = await api.deleteFile(r.id, f.id);
    setBusy(false);
    setDeleting(null);
    if (!res.ok) return show('Could not delete. Reload the page.');
    show(deletedText(f.fileName), async () => {
      const back = await api.restoreFile(r.id, f.id);
      show(back.ok ? restoredText(f.fileName) : cannotUndoText);
      onChanged();
    });
    onChanged();
  }

  return (
    <section className="card" aria-labelledby="fl-title">
      <div className="card-h">
        <h2 id="fl-title" className="card-t">
          <FileIcon className="ico s14" />
          Files
        </h2>
        <button
          type="button"
          className="btn btn-sm"
          aria-haspopup="dialog"
          disabled={!works}
          onClick={() => setAdding(true)}
        >
          <UploadIcon className="ico s12" />
          Upload PDF
        </button>
      </div>
      {docs.length === 0 && added.length === 0 ? (
        <div className="emp">
          <FileIcon className="ico s14" />
          <span>No files yet. The dec page shows here once it is in.</span>
        </div>
      ) : (
        <div>
          {files.current.map((d) => fileRow(d))}
          {added.map((f) => addedRow(f))}
          {files.lastTerm.length > 0 && (
            <LastTermFold inset>{files.lastTerm.map((d) => fileRow(d))}</LastTermFold>
          )}
        </div>
      )}
      {file && <Viewer {...file} onClose={() => setFile(null)} />}
      {adding && (
        <FileUploadDialog
          api={api}
          r={r}
          onClose={() => setAdding(false)}
          onAdded={(result, name) => {
            setAdding(false);
            const id = result.file?.id;
            if (result.duplicate || !id) {
              show(alreadyThereText);
            } else {
              // Undo takes the new file off again, as a delete the Portal log shows.
              show(uploadedFileText(name), async () => {
                const back = await api.deleteFile(r.id, id);
                show(back.ok ? deletedText(name) : cannotUndoText);
                onChanged();
              });
            }
            onChanged();
          }}
        />
      )}
      {deleting && (
        <ConfirmPopup
          title={deleteTitle(deleting.fileName)}
          line={deleteLine}
          action="Delete"
          danger
          busy={busy}
          onConfirm={() => void remove(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={toast.undo ? { label: 'Undo', onClick: () => void toast.undo!() } : undefined}
          shownMs={toast.undo ? 8000 : undefined}
        />
      )}
    </section>
  );
}

/** Portal log: the renewal's own activity, newest first, read only (spec C7.4, C15). */
export function PortalLogCard({ entries }: { entries: LogEntry[] | null }) {
  return (
    <section className="card" aria-labelledby="log-title">
      <div className="card-h">
        <h2 id="log-title" className="card-t">
          <svg className="ico s14" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
          Portal log
        </h2>
        <span className="t12 muted">Read only</span>
      </div>
      {entries === null ? (
        <div className="emp">
          <span>The Portal log could not be loaded. Reload the page to try again.</span>
        </div>
      ) : (
        <div>
          {entries.map((e, i) => (
            <LogLine key={i} e={e} className="logl" />
          ))}
        </div>
      )}
    </section>
  );
}

export function LogLine({ e, className }: { e: LogEntry; className: string }) {
  return (
    <div className={className}>
      <span className="muted">{stampTime(e.at)}</span>
      <span>
        <span className="ink">{e.text}</span> <span className="muted">by {e.who}</span>
      </span>
    </div>
  );
}

/** The Quick view's steps: "N of 6 done" (5 without Proof to bank) and each step with its state
 * (Renewals board). */
export function QuickSteps({ steps }: { steps: StepsView }) {
  const shown = shownSteps(steps);
  const states = [
    steps.decPage.attached || steps.decPage.previously ? 'done' : 'open',
    ...shown.map((k) => steps.steps[k].state),
  ];
  const done = states.filter((s) => s === 'done').length;
  const now = states.findIndex((s) => s !== 'done');
  const names = ['Dec page', ...shown.map((k) => stepTitle(k, steps.amsStep))];
  return (
    <section aria-labelledby="qv-s" className="col g4">
      <div className="row between">
        <h3 id="qv-s" className="h3">
          Steps
        </h3>
        <span className="t12 muted">
          {done} of {states.length} done
        </span>
      </div>
      <div>
        {states.map((s, i) => {
          const ok = s === 'done';
          return (
            <div key={i} className="qstep">
              <span className={`num ${ok ? 'num-done' : i === now ? 'num-now' : 'num-next'}`}>
                {ok ? <CheckIcon className="ico s12" /> : i + 1}
              </span>
              <span className={ok || i === now ? 'grow ink' : 'grow muted'}>{names[i]}</span>
              {s === 'waiting' && <span className="stamp now">Waiting on the insured</span>}
              {i === now && s !== 'waiting' && <span className="stamp now">Current step</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
